import { create } from 'zustand';
import { DAMNodeType, type TreeRecursiveChildrenNode } from '@/types/tree';
import { isAsset } from '@/lib/utils';

type StdNode = {
  id: string;
  name: string;
  slug: string;
};

type FolderNode = StdNode & {
  type: DAMNodeType.Folder;
  children: Array<DAMNode>;
};

type AlbumNode = StdNode & {
  type: DAMNodeType.Album;
};

type AssetNode = StdNode & {
  type: DAMNodeType.Asset;
};

export type DAMNode = FolderNode | AlbumNode | AssetNode;

type NodePath = Array<string>;

type MinimalDAMNode = { id: string; name: string };

type SelectedNode = {
  type: DAMNodeType;
  node: DAMNode | MinimalDAMNode;
};

type TreeSelection = { id: string; type: DAMNodeType; label: string };

interface TreeStore {
  selectedNode: SelectedNode | undefined;
  setSelectedNode: (type: DAMNodeType | undefined, node: DAMNode | MinimalDAMNode | undefined | null) => void;
  reusableTreeSelectedFolder: string | undefined;
  setReusableTreeSelectedFolder: (string: string | undefined) => void;
  rootFolders: Array<DAMNode>;
  setRootFolders: (folders: Array<DAMNode>) => void;
  findNodePath: (nodeId: string, returnValue?: keyof DAMNode) => NodePath | null;
  findNodePathNodes: (nodeId: string) => Array<DAMNode> | null;
  findParentNode: (nodeId: string) => DAMNode | null;
  reusableTreeSelection?: TreeSelection;
  setReusableTreeSelection: (sel?: TreeSelection) => void;
  searchState: Record<string, { searchTerm: string; committedTerm: string }>;
  setSearchTerm: (value: string, scope?: string) => void;
  setCommittedTerm: (value: string, scope?: string) => void;
  reset: (scope?: string) => void;
}

type SearchState = { searchTerm: string; committedTerm: string };

const DEFAULT_SEARCH_SCOPE = 'default';
const createEmptySearchState = (): SearchState => ({ searchTerm: '', committedTerm: '' });

/**
 * The sidebar's assembled tree, in the shape the path lookups below walk.
 *
 * Convert it whole. Writing one folder's children at a time drops what was loaded below them, and
 * `findNodePath` then cannot resolve a deep path.
 */
export const toDAMNodes = (nodes: Array<TreeRecursiveChildrenNode>): Array<DAMNode> =>
  nodes.map((entry) => {
    const { id, name, slug } = entry.node;

    if (entry.type === DAMNodeType.Folder) {
      return { id, name, slug, type: DAMNodeType.Folder, children: toDAMNodes(entry.children ?? []) };
    }

    return { id, name, slug, type: isAsset(entry.type) ? DAMNodeType.Asset : DAMNodeType.Album };
  });

export const getAllDescendantIds = (nodeId: string, rootFolders: Array<DAMNode>): Array<string> => {
  const descendantIds: Array<string> = [];

  const findNodeAndCollectDescendants = (nodes: Array<DAMNode>): boolean => {
    for (const node of nodes) {
      if (node.id === nodeId) {
        // Found the node, collect all descendant IDs
        const collectIds = (n: DAMNode): void => {
          if (n.type === DAMNodeType.Folder && n.children) {
            n.children.forEach((child) => {
              descendantIds.push(child.id);
              collectIds(child);
            });
          }
        };
        collectIds(node);
        return true;
      }

      if (node.type === DAMNodeType.Folder && node.children) {
        if (findNodeAndCollectDescendants(node.children)) {
          return true;
        }
      }
    }
    return false;
  };

  findNodeAndCollectDescendants(rootFolders);
  return descendantIds;
};

export const useTreeStore = create<TreeStore>()((set, get) => ({
  // Initial state with root folders
  selectedNode: undefined,
  setSelectedNode: (type, node) => {
    set({
      selectedNode: type && node ? { type, node } : undefined,
    });
  },
  reusableTreeSelectedFolder: undefined,
  setReusableTreeSelectedFolder: (reusableTreeSelectedFolder) => set({ reusableTreeSelectedFolder }),

  reusableTreeSelection: undefined,
  setReusableTreeSelection: (reusableTreeSelection) => set({ reusableTreeSelection }),

  rootFolders: [],
  setRootFolders: (folders) => set({ rootFolders: folders }),

  // Find the path to a specific node by its ID, as a single field per ancestor
  findNodePath: (nodeId: string, returnValue: keyof DAMNode = 'slug') =>
    get()
      .findNodePathNodes(nodeId)
      ?.map((node) => node[returnValue]) ?? null,

  // Walks the tree from the root to find nodeId, returning the full ancestor chain.
  findNodePathNodes: (nodeId: string) => {
    const findPath = (nodes: Array<DAMNode>, path: Array<DAMNode>): Array<DAMNode> | null => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          return [...path, node];
        }

        if (node.type === DAMNodeType.Folder && node.children) {
          const result = findPath(node.children, [...path, node]);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };
    return findPath(get().rootFolders, []);
  },

  findParentNode: (nodeId: string) => {
    const findParent = (nodes: Array<DAMNode>, parent: DAMNode): DAMNode | null => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          return parent;
        }

        if (node.type === DAMNodeType.Folder) {
          const result = findParent(node.children, node);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };
    return findParent(get().rootFolders, null as unknown as DAMNode);
  },

  searchState: {
    [DEFAULT_SEARCH_SCOPE]: createEmptySearchState(),
  },
  setSearchTerm: (searchTerm, scope = DEFAULT_SEARCH_SCOPE) =>
    set((state) => ({
      searchState: {
        ...state.searchState,
        [scope]: {
          ...(state.searchState[scope] ?? createEmptySearchState()),
          searchTerm,
        },
      },
    })),
  setCommittedTerm: (committedTerm, scope = DEFAULT_SEARCH_SCOPE) =>
    set((state) => ({
      searchState: {
        ...state.searchState,
        [scope]: {
          ...(state.searchState[scope] ?? createEmptySearchState()),
          committedTerm,
        },
      },
    })),
  reset: (scope = DEFAULT_SEARCH_SCOPE) =>
    set((state) => ({
      searchState: {
        ...state.searchState,
        [scope]: createEmptySearchState(),
      },
    })),
}));
